import { z } from "zod";
import { LIMITS } from "../constants/gameConstants.js";
import type { VehicleBlueprint } from "../types/VehicleBlueprint.js";
import { computeStatBudget, type StatBudgetReport } from "./statBudget.js";

const HEX_COLOR = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

const colorSchema = z
  .string()
  .regex(HEX_COLOR, "color must be #RRGGBB or #RRGGBBAA");

const range = (n: { min: number; max: number }) =>
  z.number().finite().min(n.min).max(n.max);

const StyleSchema = z
  .object({
    primaryColor: colorSchema,
    secondaryColor: colorSchema,
    trimColor: colorSchema,
    wireframe: z.boolean(),
    glow: z.boolean(),
  })
  .strict();

const BodySchema = z
  .object({
    length: range(LIMITS.body.length),
    width: range(LIMITS.body.width),
    height: range(LIMITS.body.height),
    shape: z.enum(["wedge", "boxy", "muscle", "rocket", "buggy"]),
    spoiler: z.boolean(),
  })
  .strict();

const WheelsSchema = z
  .object({
    size: range(LIMITS.wheels.size),
    width: range(LIMITS.wheels.width),
    style: z.enum(["neon-rim", "spoked", "solid", "hover-pad"]),
  })
  .strict();

const WeaponSchema = z
  .object({
    type: z.enum(["standard-plasma", "rapid-blaster", "heavy-cannon", "long-range-rail", "rocket-launcher"]),
    damage: range(LIMITS.weapon.damage),
    reloadMs: range(LIMITS.weapon.reloadMs),
    projectileSpeed: range(LIMITS.weapon.projectileSpeed),
    range: range(LIMITS.weapon.range),
    projectileLifetimeMs: range(LIMITS.weapon.projectileLifetimeMs),
    maxBounces: z.number().int().min(0).max(10).optional(),
  })
  .strict();

const HandlingSchema = z
  .object({
    maxSpeed: range(LIMITS.handling.maxSpeed),
    acceleration: range(LIMITS.handling.acceleration),
    braking: range(LIMITS.handling.braking),
    turnRate: range(LIMITS.handling.turnRate),
    traction: range(LIMITS.handling.traction),
    drift: range(LIMITS.handling.drift),
  })
  .strict();

const StatsSchema = z
  .object({
    maxHealth: range(LIMITS.stats.maxHealth),
    armour: range(LIMITS.stats.armour),
    mass: range(LIMITS.stats.mass),
  })
  .strict();

export const VehicleBlueprintSchema = z
  .object({
    id: z.string().min(2).max(48).regex(/^[a-z0-9-]+$/, "id must be kebab-case ascii"),
    // Reject angle brackets and control characters so a name can never carry an
    // HTML tag into any current or future render path (defense-in-depth XSS guard).
    name: z.string().min(1).max(40).regex(/^[^<>\u0000-\u001f\u007f]+$/, "name contains invalid characters"),
    style: StyleSchema,
    body: BodySchema,
    wheels: WheelsSchema,
    weapon: WeaponSchema,
    weapon2: WeaponSchema.optional(),
    handling: HandlingSchema,
    stats: StatsSchema,
  })
  .strict();

export type ValidationOk = {
  ok: true;
  blueprint: VehicleBlueprint;
  budget: StatBudgetReport;
};

export type ValidationErr = {
  ok: false;
  errors: string[];
  budget?: StatBudgetReport;
};

export type ValidationResult = ValidationOk | ValidationErr;

export function validateVehicleBlueprint(input: unknown): ValidationResult {
  const parsed = VehicleBlueprintSchema.safeParse(input);
  if (!parsed.success) {
    const errors = parsed.error.issues.map(
      (i) => `${i.path.join(".") || "<root>"}: ${i.message}`,
    );
    return { ok: false, errors };
  }

  const blueprint = parsed.data as VehicleBlueprint;
  const budget = computeStatBudget(blueprint);
  if (!budget.withinBudget) {
    return {
      ok: false,
      errors: [
        `vehicle exceeds stat budget: ${budget.total.toFixed(1)} / ${budget.budget}`,
      ],
      budget,
    };
  }
  return { ok: true, blueprint, budget };
}
